import { Component, inject, OnDestroy, OnInit } from '@angular/core';
import { EquipmentService } from '../equipment.service';
import { Button } from 'primeng/button';
import { Drawer } from 'primeng/drawer';
import { AsyncPipe, CurrencyPipe, DecimalPipe } from '@angular/common';
import { Equipment, EquipmentUpdate } from '../../models/equipment.model';
import {
  FormControl,
  FormGroup,
  ReactiveFormsModule,
  Validators,
} from '@angular/forms';
import { isPermissionError, parseApiError } from '../quotes-api-error';
import { InputTextModule } from 'primeng/inputtext';
import { InputNumberModule } from 'primeng/inputnumber';
import { ChipModule } from 'primeng/chip';
import { CheckboxModule } from 'primeng/checkbox';
import { TextareaModule } from 'primeng/textarea';
import { FloatLabelModule } from 'primeng/floatlabel';
import { Tooltip } from 'primeng/tooltip';
import { combineLatest, map, startWith, Subscription } from 'rxjs';
import { SharedConfirmDialogComponent } from '@tangible/shared';
import { ConfirmationService } from 'primeng/api';
import { SettingsService } from '../settings-page/settings-service/settings.service';
import { Message } from 'primeng/message';
import { MultiSelectModule } from 'primeng/multiselect';
import { SelectModule } from 'primeng/select';
import { IconFieldModule } from 'primeng/iconfield';
import { InputIconModule } from 'primeng/inputicon';

@Component({
  selector: 'app-equipment-page',
  imports: [
    Button,
    Drawer,
    AsyncPipe,
    CurrencyPipe,
    DecimalPipe,
    ReactiveFormsModule,
    InputTextModule,
    InputNumberModule,
    CheckboxModule,
    TextareaModule,
    FloatLabelModule,
    Tooltip,
    SharedConfirmDialogComponent,
    ChipModule,
    Message,
    MultiSelectModule,
    SelectModule,
    IconFieldModule,
    InputIconModule,
  ],
  providers: [ConfirmationService],
  templateUrl: './equipment-page.component.html',
  styleUrl: './equipment-page.component.scss',
})
export class EquipmentPageComponent implements OnInit, OnDestroy {
  equipmentService = inject(EquipmentService);
  settingsService = inject(SettingsService);
  confirmationService = inject(ConfirmationService);

  isDrawerVisible = false;
  equipmentInDraw: Equipment | null = null;
  includeArchived = new FormControl<boolean>(false);
  saveError: string | null = null;
  expandedRows = new Set<number>();

  toggleRow(id: number): void {
    if (this.expandedRows.has(id)) {
      this.expandedRows.delete(id);
    } else {
      this.expandedRows.add(id);
    }
  }

  searchQuery = new FormControl<string>('');
  selectedTypeFilters = new FormControl<string[]>([]);
  selectedMarketFilters = new FormControl<string[]>([]);

  private subscriptions = new Subscription();

  currencyCode$ = this.settingsService.settings$.pipe(
    map((settings) => settings?.organisation_base_currency_code || 'GBP'),
  );

  private allEquipment$ = combineLatest([
    this.includeArchived.valueChanges.pipe(
      startWith(this.includeArchived.value),
    ),
    this.equipmentService.activeEquipment$,
    this.equipmentService.equipment$,
  ]).pipe(
    map(([includeArchived, active, all]) => (includeArchived ? all : active)),
  );

  typeFilterOptions$ = this.equipmentService.equipment$.pipe(
    map((all) => {
      const types = Array.from(
        new Set(all.map((e) => e.equipment_type).filter(Boolean)),
      ).sort() as string[];
      return types.map((t) => ({ label: t, value: t }));
    }),
  );

  marketFilterOptions$ = this.equipmentService.equipment$.pipe(
    map((all) => {
      const markets = Array.from(
        new Set(all.map((e) => e.market).filter(Boolean)),
      ).sort() as string[];
      return markets.map((m) => ({ label: m, value: m }));
    }),
  );

  tableEquipment$ = combineLatest([
    this.allEquipment$,
    this.searchQuery.valueChanges.pipe(startWith(this.searchQuery.value)),
    this.selectedTypeFilters.valueChanges.pipe(
      startWith(this.selectedTypeFilters.value),
    ),
    this.selectedMarketFilters.valueChanges.pipe(
      startWith(this.selectedMarketFilters.value),
    ),
  ]).pipe(
    map(([equipment, search, types, markets]) => {
      let result = equipment;

      const q = (search ?? '').trim().toLowerCase();
      if (q) {
        result = result.filter(
          (e) =>
            e.name.toLowerCase().includes(q) ||
            (e.equipment_type ?? '').toLowerCase().includes(q) ||
            (e.market ?? '').toLowerCase().includes(q) ||
            (e.supplier ?? '').toLowerCase().includes(q) ||
            (e.notes ?? '').toLowerCase().includes(q),
        );
      }

      if (types && types.length > 0) {
        result = result.filter((e) => types.includes(e.equipment_type ?? ''));
      }

      if (markets && markets.length > 0) {
        result = result.filter((e) => markets.includes(e.market ?? ''));
      }

      return result;
    }),
  );

  get hasActiveFilters(): boolean {
    const types = this.selectedTypeFilters.value;
    const markets = this.selectedMarketFilters.value;
    return (
      (this.searchQuery.value ?? '').trim().length > 0 ||
      (types !== null && types.length > 0) ||
      (markets !== null && markets.length > 0)
    );
  }

  clearFilters(): void {
    this.searchQuery.setValue('');
    this.selectedTypeFilters.setValue([]);
    this.selectedMarketFilters.setValue([]);
  }

  equipmentFormGroup = new FormGroup({
    equipmentType: new FormControl('', [Validators.required]),
    name: new FormControl('', [Validators.required]),
    market: new FormControl(''),
    supplier: new FormControl(''),
    source: new FormControl(''),
    capexCost: new FormControl<number | null>(null),
    amortizationYears: new FormControl<number | null>(null),
    annualOperatingCostRatePercent: new FormControl<number | null>(9),
    maxPartsPerProcess: new FormControl<number | null>(null),
    footprintSqFt: new FormControl<number | null>(null),
    powerConsumptionKwh: new FormControl<number | null>(null),
    notes: new FormControl(''),
  });

  ngOnInit(): void {
    void this.equipmentService.loadEquipment();
  }

  ngOnDestroy(): void {
    this.subscriptions.unsubscribe();
  }

  private numToPayload(n: number | null): number | null {
    return n === null || n === undefined || Number.isNaN(n) ? null : n;
  }

  private decimalFromRow(value: string | null): number | null {
    if (value === null || value === '') return null;
    const n = Number(value);
    return Number.isFinite(n) ? n : null;
  }

  private numToApiDecimal(
    n: number | null,
    fractionDigits?: number,
  ): string | null {
    const v = this.numToPayload(n);
    if (v === null) return null;
    if (fractionDigits !== undefined) {
      return v.toFixed(fractionDigits);
    }
    return String(v);
  }

  private controlNumber(
    name: keyof typeof this.equipmentFormGroup.controls,
  ): number | null {
    const value = this.equipmentFormGroup.controls[name].value;
    return typeof value === 'number' && Number.isFinite(value) ? value : null;
  }

  get calculatedEquipmentAvailabilityHoursPerYear(): number | null {
    return this.equipmentInDraw
      ? this.displayMoney(
          this.equipmentInDraw.equipment_availability_hours_per_year,
        )
      : null;
  }

  get calculatedAmortizationPerHour(): number | null {
    const capexCost = this.controlNumber('capexCost');
    const amortizationYears = this.controlNumber('amortizationYears');
    const availability = this.calculatedEquipmentAvailabilityHoursPerYear;
    const maxPartsPerProcess = this.controlNumber('maxPartsPerProcess');

    if (
      capexCost === null ||
      amortizationYears === null ||
      availability === null ||
      maxPartsPerProcess === null
    ) {
      return null;
    }

    const denominator = amortizationYears * availability * maxPartsPerProcess;
    return denominator === 0 ? null : capexCost / denominator;
  }

  get calculatedRunCostPerHour(): number | null {
    const capexCost = this.controlNumber('capexCost');
    const annualOperatingCostRatePercent = this.controlNumber(
      'annualOperatingCostRatePercent',
    );
    const availability = this.calculatedEquipmentAvailabilityHoursPerYear;
    const maxPartsPerProcess = this.controlNumber('maxPartsPerProcess');

    if (
      capexCost === null ||
      annualOperatingCostRatePercent === null ||
      availability === null ||
      maxPartsPerProcess === null
    ) {
      return null;
    }

    const denominator = availability * maxPartsPerProcess;
    return denominator === 0
      ? null
      : (capexCost * (annualOperatingCostRatePercent / 100)) / denominator;
  }

  openDrawer(equipment: Equipment | null = null): void {
    this.saveError = null;
    this.equipmentInDraw = equipment;
    if (equipment) {
      this.equipmentFormGroup.patchValue({
        equipmentType: equipment.equipment_type,
        name: equipment.name,
        market: equipment.market,
        supplier: equipment.supplier,
        source: equipment.source,
        capexCost: this.decimalFromRow(equipment.capex_cost),
        amortizationYears: equipment.amortization_years,
        annualOperatingCostRatePercent:
          (this.decimalFromRow(equipment.annual_operating_cost_rate) ?? 0.09) *
          100,
        maxPartsPerProcess: equipment.max_parts_per_process,
        footprintSqFt: this.decimalFromRow(equipment.footprint_sq_ft),
        powerConsumptionKwh: this.decimalFromRow(
          equipment.power_consumption_kwh,
        ),
        notes: equipment.notes,
      });
    } else {
      this.equipmentFormGroup.reset({
        equipmentType: '',
        name: '',
        market: '',
        supplier: '',
        source: '',
        capexCost: null,
        amortizationYears: null,
        annualOperatingCostRatePercent: 9,
        maxPartsPerProcess: null,
        footprintSqFt: null,
        powerConsumptionKwh: null,
        notes: '',
      });
    }
    this.isDrawerVisible = true;
  }

  closeDrawer(): void {
    this.isDrawerVisible = false;
    this.equipmentInDraw = null;
  }

  private buildPayload(): EquipmentUpdate {
    const v = this.equipmentFormGroup.getRawValue();
    return {
      equipment_type: v.equipmentType ?? '',
      name: v.name ?? '',
      market: v.market ?? '',
      supplier: v.supplier ?? '',
      source: v.source ?? '',
      capex_cost: this.numToApiDecimal(v.capexCost ?? null, 2),
      amortization_years: this.numToPayload(v.amortizationYears ?? null),
      annual_operating_cost_rate:
        this.numToApiDecimal(
          v.annualOperatingCostRatePercent === null ||
            v.annualOperatingCostRatePercent === undefined
            ? null
            : v.annualOperatingCostRatePercent / 100,
          5,
        ) ?? '0.09000',
      max_parts_per_process: this.numToPayload(v.maxPartsPerProcess ?? null),
      footprint_sq_ft: this.numToApiDecimal(v.footprintSqFt ?? null, 2),
      power_consumption_kwh: this.numToApiDecimal(
        v.powerConsumptionKwh ?? null,
        2,
      ),
      notes: v.notes ?? '',
      is_archived: this.equipmentInDraw?.is_archived ?? false,
    };
  }

  async saveEquipment(): Promise<void> {
    if (this.equipmentFormGroup.invalid) {
      this.equipmentFormGroup.markAllAsTouched();
      return;
    }
    this.saveError = null;
    const payload = this.buildPayload();
    try {
      if (this.equipmentInDraw) {
        await this.equipmentService.putEquipment(this.equipmentInDraw.id, {
          ...payload,
        });
      } else {
        await this.equipmentService.createEquipment(payload);
      }
      this.closeDrawer();
    } catch (err: unknown) {
      if (isPermissionError(err)) {
        this.saveError = parseApiError(err);
      } else {
        this.saveError = parseApiError(err);
      }
    }
  }

  openConfirm(
    event: Event,
    equipment: Equipment,
    action: 'archive' | 'unarchive',
  ): void {
    const isArchive = action === 'archive';

    this.confirmationService.confirm({
      target: event.target as EventTarget,
      message: `Are you sure that you want to ${action} ${equipment.name}?`,
      header: `${isArchive ? 'Archive' : 'Unarchive'} ${equipment.name}`,
      closable: true,
      closeOnEscape: true,
      icon: isArchive ? 'pi pi-exclamation-triangle' : 'pi pi-refresh',
      rejectButtonProps: {
        label: 'Cancel',
        severity: 'secondary',
        outlined: true,
      },
      acceptButtonProps: {
        label: isArchive ? 'Archive' : 'Unarchive',
      },
      accept: () => {
        void this.handleEquipmentAction(action, equipment);
      },
    });
  }

  private async handleEquipmentAction(
    action: 'archive' | 'unarchive',
    equipment: Equipment,
  ): Promise<void> {
    try {
      const fn =
        action === 'archive'
          ? this.equipmentService.archiveEquipment
          : this.equipmentService.unarchiveEquipment;

      await fn.call(this.equipmentService, equipment.id);
    } catch (err: unknown) {
      this.saveError = parseApiError(err);
    }
  }

  displayMoney(value: string | null): number | null {
    if (value === null || value === '') return null;
    const n = Number(value);
    return Number.isFinite(n) ? n : null;
  }

  displayEfficiencyPercent(value: string | null): string {
    if (value === null || value === '') return '—';
    const n = Number(value);
    if (!Number.isFinite(n)) return '—';
    return `${(n * 100).toFixed(0)}%`;
  }
}
